import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { User, Mail, Phone, Edit3, Save, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export default function UserProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'event_manager': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'operations_manager': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'client': return 'bg-[#B8860B]/20 text-[#B8860B] border-[#B8860B]/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'event_manager': return 'Event Manager';
      case 'operations_manager': return 'Operations Manager';
      case 'client': return 'Client';
      default: return role;
    }
  };

  const updateProfileMutation = useMutation({
    mutationFn: (data: typeof profileData) => 
      apiRequest(`/api/users/profile`, { method: 'PUT', data }),
    onSuccess: () => {
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['/api/users/me'] });
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateProfileMutation.mutate(profileData);
  };

  const handleCancel = () => {
    setProfileData({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
    });
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-[#B8860B] mb-2">User Profile</h1>
            <p className="text-white/70">Manage your account information and preferences</p>
          </div>
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              className="bg-[#B8860B] hover:bg-[#B8860B]/80 text-black"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                onClick={handleSave}
                disabled={updateProfileMutation.isPending}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-gray-600 text-white hover:bg-gray-800"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-6">
          {/* Profile Overview Card */}
          <Card className="bg-gray-900 border-[#B8860B]/30">
            <CardHeader>
              <CardTitle className="text-[#B8860B] flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start space-x-6">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${user?.firstName} ${user?.lastName}`} />
                  <AvatarFallback className="bg-[#B8860B] text-black text-xl">
                    {getInitials(user?.firstName || '', user?.lastName || '')}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 space-y-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-2xl font-semibold text-white">
                      {user?.firstName} {user?.lastName}
                    </h3>
                    <Badge className={`${getRoleColor(user?.role || '')} border`}>
                      {getRoleDisplayName(user?.role || '')}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 text-sm">First Name</Label>
                      {isEditing ? (
                        <Input
                          value={profileData.firstName}
                          onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                          className="mt-1 bg-black/60 border-[#B8860B]/30 text-white"
                        />
                      ) : (
                        <p className="text-white mt-1">{user?.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-white/70 text-sm">Last Name</Label>
                      {isEditing ? (
                        <Input
                          value={profileData.lastName}
                          onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                          className="mt-1 bg-black/60 border-[#B8860B]/30 text-white"
                        />
                      ) : (
                        <p className="text-white mt-1">{user?.lastName}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-white/70 text-sm flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        Email Address
                      </Label>
                      {isEditing ? (
                        <Input
                          value={profileData.email}
                          onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                          className="mt-1 bg-black/60 border-[#B8860B]/30 text-white"
                          type="email"
                        />
                      ) : (
                        <p className="text-white mt-1 break-all">{user?.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-white/70 text-sm flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        Phone Number
                      </Label>
                      {isEditing ? (
                        <Input
                          value={profileData.phone}
                          onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                          className="mt-1 bg-black/60 border-[#B8860B]/30 text-white"
                          type="tel"
                        />
                      ) : (
                        <p className="text-white mt-1">{user?.phone || 'Not provided'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Details Card */}
          <Card className="bg-gray-900 border-[#B8860B]/30">
            <CardHeader>
              <CardTitle className="text-[#B8860B]">Account Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70 text-sm">User ID</Label>
                  <p className="text-white mt-1">#{user?.id}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-sm">Account Type</Label>
                  <p className="text-white mt-1">{getRoleDisplayName(user?.role || '')}</p>
                </div>
                <div>
                  <Label className="text-white/70 text-sm">Member Since</Label>
                  <p className="text-white mt-1">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-white/70 text-sm">Last Updated</Label>
                  <p className="text-white mt-1">
                    {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}